// @ts-check
import React, { useCallback } from 'react';
import { useMutation } from 'react-query';
import api from 'utils/api';
import { Button } from 'preferral-ui/components/Button';
import { FormStatusErrors } from 'preferral-ui/components/formik/FormStatusErrors';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { TextAreaInput } from 'preferral-ui/components/formik/TextAreaField';
import { PlainSelectInput } from 'preferral-ui/components/formik/PlainSelectField';
import { usePortalConfig } from 'shared/context/PortalConfig';

/** @typedef {{
  comment: string;
  visibility: string;
}} FormValues */

/** @type { Yup.SchemaOf<FormValues> } */
const validationSchema = Yup.object()
  .shape({
    comment: Yup.string().trim().required('Required'),
    visibility: Yup.string().trim().required('Required'),
  })
  .required();

const visibilityOptions = [
  {
    value: 'everyone',
    label: 'Visible to All',
  },
  {
    value: 'internal_only',
    label: 'Internal Only',
  },
];

/** @typedef {{
  referralId: string;
  onSuccess(): void;
}} CommentFormProps */

/** @type { React.FC<CommentFormProps> } */
export const CommentForm = (props) => {
  const { referralId, onSuccess } = props;

  const { apiPrefix } = usePortalConfig();

  const mutation = useMutation((/** @type { FormValues } */ values) =>
    api
      .post(`${apiPrefix}/referrals/${referralId}/referral_comments`, values)
      .then((resp) => resp.data)
  );

  const onSubmit = useCallback(
    (values, formikHelpers) => {
      const { setStatus, setSubmitting, resetForm } = formikHelpers;

      setStatus({ errors: null });

      return mutation.mutateAsync(values).then(
        (data) => {
          resetForm();
          // Flash message?
          onSuccess();
        },
        (err) => {
          setSubmitting(false);
          if (err && err.errors && err.errors._error) {
            setStatus({
              errors: err.errors._error.map((e) => ({ key: '', message: e })),
            });
          } else {
            return Promise.reject(err);
          }
        }
      );
    },
    [onSuccess, mutation]
  );

  return (
    <div className="_CommentForm max-w-3xl mx-auto">
      <Formik
        initialValues={{
          comment: '',
          visibility: 'everyone',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ status, isSubmitting, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <FormStatusErrors status={status} />
            <div>
              <label htmlFor="comment" className="sr-only">
                Comment
              </label>
              <TextAreaInput
                className="textarea"
                name="comment"
                placeholder="Your comment"
              />
            </div>
            <div className="flex items-center justify-end mt-6 space-x-4">
              <p>Visibility</p>
              <div className="w-56">
                <PlainSelectInput
                  name="visibility"
                  options={visibilityOptions}
                />
              </div>
              <Button
                type="submit"
                color="blue"
                className="mr-2button is-primary"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Post Comment
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
